import React, {useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SideForm } from 'components/SideForms/SideForm';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { hideForm } from 'services/forms';
import moment from 'moment';
import { useLazyGetElementQuery, useUpdateElementMutation, useCreateElementMutation, uploadElement } from 'services/element';
import { useGetTagsQuery } from 'services/tags';

import {
  RadioButtonGroup,
  TextInput,
  TextArea,
  RadioButton,
  Form,
  FormGroup,
  TextInputSkeleton,
  TextAreaSkeleton,
  SkeletonPlaceholder,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  NumberInput,
  Link,
  UnorderedList,
  ListItem,
  Checkbox,
  DatePicker,
  DatePickerInput,
  Dropdown,
} from '@carbon/react';

import { ElementViewer } from 'components/Elements';
import { Column, Row } from 'components/Grid';
import LoadingPage from '@/pages/LoadingPage';
import Upload from 'components/Elements/Uploading';
import { showToast } from '@/redux/new_services/toasts';
import { openElementForm, openElementUploadForm } from '@/redux/new_services/forms';
import { informAboutAddedElements, informAboutChangedElements, setAsViewed } from '@/redux/new_services/element';
import { useGetOrganisationsQuery } from '@/redux/new_services/organisations';
import useUser from '@/lib/hooks/useUser';
import useModal from '@/lib/hooks/useModal';
import { SelectableGroups } from '@/components/GroupsSelection/Groups';
import ElementPairAssignment from './ElementPairAssignment';
import ElementVariantsAssignment from './ElementVariantsAssignment';

const defaultItem = {
  name: '',
  description: '',
  default_duration: 10,
  orientation: 0,
  type: 0,
  public: true,
};

const customStyles = {
  option: (provided, _state) => ({
    ...provided,
  }),
  container: (provided, _state) => ({
    ...provided,
    '&:focus': {
      borderColor: "var(--cds-focus, #0099bb)",
      borderWidth: "2px"
    },
  }),
  control: (provided, _state) => ({
    // none of react-select's styles are passed to <Control />
    ...provided,
    borderRadius: 0,
    border: 0,
    backgroundColor: "#f4f4f4",
    borderBottom: "1px solid var(--cds-border-strong)",
    '&:focus': {
      borderColor: "var(--cds-focus, #0099bb)",
      borderWidth: "2px"
    },
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = 'opacity 300ms';

    return { ...provided, opacity, transition };
  }
}

const ElementForm = ({
  id = null,
  element,
  isNew = false,
}) => {

  const today = moment();

  const [confirmRemoveElement] = useModal('DELETE_ELEMENT');
  const dispatch = useDispatch();
  const {isAdmin} = useUser();

  // Pulled from the element slice — uploadElement dispatches uploadProgress
  // actions from axios' onUploadProgress callback. Without this the
  // LoadingPage rendered without a ProgressBar and the upload looked frozen.
  const current_upload_progress = useSelector(
    (state) => state.element?.upload_progress || 0,
  );
  const is_saving = false;

  const [isUploading, setIsUploading] = useState(false);

  const [imageLoaded, setImageLoaded] = useState(false);
  const [item, setItem] = useState(null);
  const [editable, setEditable] = useState(false);
  const [unsaved, setUnsaved] = useState(isNew);
  const [image, setImage] = useState({});
  const [categoryTextInput, setCategoryTextInput] = useState('');
  const [customCategories, setCustomCategories] = useState([]);
  const [newfile, setNewfile] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [replacedImage, setReplaceImage] = useState(false);

  const [inStreamStatus, setInStreamStatus] = useState({streams: [], count: 0});

  const isMultiupload = Object.prototype.toString.call(element) === '[object Array]' && element.length > 1 && isNew === true;

  // Safely extract a human-readable hint from whatever shape a rejected
  // promise hands us. RTK Query mutations resolve their .unwrap() rejection
  // as { status, data, payload? } — but baseQuery rewrites this depending
  // on the network failure mode, so blindly reading rejectError.payload.data
  // crashes on TypeError. The helper walks the common shapes but always
  // returns a *string*: interpolating an object into the caption would
  // either print "[object Object]" or trip React #31 once Carbon tries
  // to render the object child of <ToastNotification>.
  const errorCaption = (e) => {
    if (!e) return 'unbekannt';
    if (typeof e === 'string') return e;
    const candidates = [
      e?.payload?.data?.message,
      e?.payload?.data?.msg,
      typeof e?.payload?.data === 'string' ? e.payload.data : null,
      e?.data?.message,
      e?.data?.msg,
      typeof e?.data === 'string' ? e.data : null,
      e?.message,
    ];
    for (const c of candidates) {
      if (typeof c === 'string' && c.length > 0) return c;
    }
    return 'unbekannt';
  };

  const {
    data: tags,
  } = useGetTagsQuery({refetchOnMountOrArgChange: true});

  // Admins can re-assign an element to a specific customer organisation so
  // it leaves the public pool (organisation_id = 0) and only that customer
  // sees it. Non-admins never need this list — their elements always belong
  // to their own org, set on the server.
  const {
    data: organisations = [],
  } = useGetOrganisationsQuery(undefined, { skip: !isAdmin });

  const [
    trigger,
    {
      isLoading,
    }
  ] = useLazyGetElementQuery({refetchOnMountOrArgChange: true});

  useEffect(() => {
    if (id) {
      setAsViewed([id]);
    }

  }, [id]);

  const [createElement, { isLoading: isCreating }] = useCreateElementMutation();
  const [updateElement, { isLoading: isSaving }] = useUpdateElementMutation();

  const load = useCallback((id) => {
    trigger(id, true)
      .unwrap()
      .then(data => {

        setItem({
          highlight_start: data.data.highlight_start && data.data.highlight_start !== '0001-01-01T00:00:00Z' ? data.data.highlight_start : '',
          highlight_end: data.data.highlight_end && data.data.highlight_end !== '0001-01-01T00:00:00Z' ? data.data.highlight_end : '',
          remove_date: data.data.remove_date && data.data.remove_date !== '0001-01-01T00:00:00Z' ? data.data.remove_date : '',
          ...data.data,
          // Pair partner is delivered alongside the element so the form can
          // render both formats side-by-side without a second roundtrip.
          _pair_partner: data.pair_partner || null,
        });

        let highlight_start_data = data.data.highlight_start;
        let highlight_end_data = data.data.highlight_end;
        if (highlight_start_data && highlight_start_data !== '0001-01-01T00:00:00Z') {
          setHighlightStart(new Date(highlight_start_data));
        }
        if (highlight_end_data && highlight_end_data !== '0001-01-01T00:00:00Z') {
          setHighlightEnd(new Date(highlight_end_data));
        }

        setEditable(data?.editable);
        setInStreamStatus({streams: data.streams || [], count: data.stream_counts || 0});
      })
      .catch((rejectError) => {
        // RTK Query aborts in-flight queries with a ConditionError when the
        // hook's skip flag flips or a tag invalidation re-triggers it —
        // benign retry signal, not a load failure. Don't toast it.
        if (rejectError?.name === 'ConditionError') return;
        dispatch(hideForm());
        dispatch(showToast({
          kind: 'error',
          title: 'Fehler beim laden',
          subtitle: `Das Element konnte nicht geladen werden.(${rejectError?.payload?.data || rejectError?.message || 'unbekannt'})`,
        }));
      });
  }, [trigger, dispatch]);

  const onRequestClose = () => {
    dispatch(hideForm());
  }

  const onSaveRequest = () => {
    if (unsaved === true) {
      if (replacedImage === true || isNew === true) {
        //if (image !== undefined && image.filename !== undefined) {
        //  image.filename = image.file.name;
        //}
        setIsUploading(true);
        fetch(image.blob)
          .then(res => res.blob())
          .then(blob => {
            let f = new File([blob], image.file.name, {type: image.file.type});
            dispatch(uploadElement(f))
              .unwrap()
              .then((res) => {
                if (isNew) {
                  setIsUploading(false);
                  // it is new, so we want to create it
                  createElement({
                    item, file: {
                      content_type: res.data.content_type,
                      filename: res.data.filename,
                      real_filename: res.data.real_filename,
                    },
                    resolution: image.resolution,
                    rotate: rotation
                  }).unwrap()
                    .then((res) => {
                      dispatch(showToast({kind: 'success', title: 'Element wurde erstellt', subtitle: 'Das Element wurde erfolgreich erstellt.'}))
                      const newId = res?.data?.model?.ID;
                      if (newId) {
                        dispatch(informAboutAddedElements([newId]));
                      }
                      setUnsaved(false);

                      if (isMultiupload) {
                        // multi upload...
                        let next = [...element];
                        next.shift(1);
                        dispatch(openElementUploadForm(next));
                      } else if (newId) {
                        dispatch(openElementForm(newId));
                      } else {
                        dispatch(hideForm());
                      }
                    })
                    .catch((rejectError) => {
                      dispatch(showToast({kind: 'error', title: 'Fehler beim erstellen', subtitle: `Das Element konnte nicht erstellt werden.(${errorCaption(rejectError)})`}))
                    });
                } else {
                  // it is not new, so we want to update it
                  updateElement({id: item.model.ID, item, replaced_file: {
                    content_type: res.data.content_type,
                    filename: res.data.filename,
                    real_filename: res.data.real_filename,
                  }, replaced_resolution: image.resolution}).unwrap().then((res) => {
                    dispatch(showToast({kind: 'success', title: 'Element wurde bearbeitet', subtitle: 'Das Element wurde erfolgreich bearbeitet.'}))
                    dispatch(informAboutChangedElements([res.data.model.ID]));
                    setUnsaved(false);

                  }).catch((rejectError) => {
                    dispatch(showToast({kind: 'error', title: 'Fehler beim bearbeiten', subtitle: `Das Element konnte nicht bearbeitet werden.(${errorCaption(rejectError)})`}))
                  });
                }
              })
              .catch((rejectError) => {
                // Reached when uploadElement itself failed (the thunk
                // rejectWithValue's the server / network error). We never get
                // here on a normal create/update failure — those toast from
                // their own .catch above.
                dispatch(showToast({
                  kind: 'error',
                  title: 'Upload fehlgeschlagen',
                  subtitle: `Die Datei konnte nicht hochgeladen werden. (${
                    typeof rejectError === 'string'
                      ? rejectError
                      : rejectError?.message || 'unbekannt'
                  })`,
                }));
              })
              .finally(() => {

                setIsUploading(false);
              });

          });
      } else {
        updateElement({id: item.model.ID, item}).unwrap().then((res) => {
          dispatch(informAboutChangedElements([res.data.model.ID]));
          dispatch(showToast({kind: 'success', title: 'Element wurde bearbeitet', subtitle: 'Das Element wurde erfolgreich bearbeitet.'}))
          setUnsaved(false);
        }).catch((rejectError) => {
          dispatch(showToast({kind: 'error', title: 'Fehler beim bearbeiten', subtitle: `Das Element konnte nicht bearbeitet werden.(${errorCaption(rejectError)})`}))
        });
      }
    }
  }

  const removeElement = (item) => {
    confirmRemoveElement({element: item})
  }

  const handleFileReplacement = (e) => {
    setReplaceImage(true);
    setImage(e[0]);
    setNewfile(false);
    setUnsaved(true);
  }

  const callbackEditElement = (position, replaceFile = false) => {
    if (replaceFile === true) {
      setNewfile(true);

      return;
    }
  }

  const changeFormField = (field, value) => {
    setItem({
      ...item,
      [field]: value,
    });
    setUnsaved(true);
  }

  const gotoStream = (id) => {
  }

  const handleAddCategory = (inputValue) => {
    setItem(item => ({
      ...item,
      tags: [...item.tags, {label: inputValue, details: {}}]
    }));
    setUnsaved(true);
  }

  const handleCategoryChange = (newValue,actionMeta) => {
    if (actionMeta.action === 'remove-value') {
      // remove value form list
      setItem(item => ({
        ...item,
        tags: item.tags.filter((e) => e.label !== actionMeta.removedValue.label),
      }));
    } else {
      setItem(item => ({
        ...item,
        tags: [...item.tags, {...actionMeta.option, details: {ID: actionMeta.option.value}}]
      }));
    }
    setUnsaved(true);
  }

  const handleChangeGroups = (groups, _preSelected) => {
    setItem(item => ({
      ...item,
      groups: groups
    }));
    setUnsaved(true);
  }


  // init

  useEffect(() => {

    let single_element = element;
    if (Object.prototype.toString.call(single_element) === '[object Array]') {
      single_element = element[0];
    }

    if (!isNew) {
      load(id);
    } else {
      setItem({
        ...defaultItem,
        name: single_element.name || '',
        needrotation:
          single_element.resolution.width === 1280 &&
          single_element.resolution.orientation === 1,
        orientation: single_element.resolution.orientation,
        default_duration: single_element.duration || 10,
        type: single_element.type || 0,
        tags: (single_element.tags || []).map((name) => {
          return { label: name, details: {} };
        }),
        remove_after: single_element.remove_after || false,
        remove_after_date: single_element.remove_after_date,
        highlight: single_element.highlight || false,
        highlight_start: single_element.highlight_start,
        highlight_end: single_element.highlight_end,
      });

      setEditable(true);
      setUnsaved(true);
      setImage(single_element);
      setRotation(0);
    }

  }, [isNew, id, element, load ]);

  let [highlight_start, setHighlightStart] = useState("");
  let [highlight_end, setHighlightEnd] = useState("");

  useEffect(() => {
    if (highlight_start !== "") {
      setItem(item => ({
        ...item,
        highlight_start: highlight_start
      }));
      setUnsaved(true);
    }
    if (highlight_end !== "") {
      setItem(item => ({
        ...item,
        highlight_end: highlight_end
      }));
      setUnsaved(true);
    }
  }, [highlight_start, highlight_end]);

  let img;
  let tab_default;
  let tab_advanced;
  let tab_usage;
  let tab_pair;
  let tab_variants;

  if (isLoading === true || !item) {
    img = (
      <SkeletonPlaceholder className="my--skeleton__placeholder--large" />
    );
    tab_default = (
      <Form>
        <FormGroup legendText="">
          <TextInputSkeleton />
        </FormGroup>
        <FormGroup legendText="">
          <TextAreaSkeleton />
        </FormGroup>
      </Form>
    );

    tab_advanced = <Form></Form>;
    tab_usage = tab_advanced;
    tab_pair = tab_advanced;
    tab_variants = tab_advanced;
  } else {
    let remove_element;

    if (editable) {
      remove_element = (
        <>
          <hr />
          <Link href="#" onClick={() => removeElement(item)}>
            Element endgültig löschen
          </Link>
        </>
      );
    }

    if (newfile !== true) {
      // If the element is half of a Hoch+Quer pair, show both formats
      // side-by-side: landscape always on the left, portrait on the right —
      // mirrors the way the pair is stored (orientation 0 / 1) and matches
      // the user's expectation from the gallery preview.
      const previewItem = !isNew && !replacedImage ? item : image;
      if (previewItem && previewItem._pair_partner) {
        const landscape = previewItem.orientation === 0 ? previewItem : previewItem._pair_partner;
        const portrait = previewItem.orientation === 1 ? previewItem : previewItem._pair_partner;
        img = (
          <div className="element-form__pair-preview-wrap">
            <div className="element-form__pair-preview">
              <div className="element-form__pair-preview-slot element-form__pair-preview-slot--landscape">
                <ElementViewer
                  item={landscape}
                  rotate={landscape === previewItem ? rotation : 0}
                  showEditing={editable && landscape === previewItem}
                  onEdit={callbackEditElement}
                />
              </div>
              <div className="element-form__pair-preview-slot element-form__pair-preview-slot--portrait">
                <ElementViewer
                  item={portrait}
                  rotate={portrait === previewItem ? rotation : 0}
                  showEditing={editable && portrait === previewItem}
                  onEdit={callbackEditElement}
                />
              </div>
            </div>
          </div>
        );
      } else {
        img = (
          <ElementViewer
            item={previewItem}
            rotate={rotation}
            showEditing={editable}
            onEdit={callbackEditElement}
          />
        );
      }
    } else {
      img = (
        <div className="element-editing--main-img-container rotation-wrapper-inner">
          <div className="element-editing-img-loading">
            <Upload
              singleUpload={true}
              dispatchActions={false}
              labelText={'Neues Element hier hin schieben'}
              handleAction={handleFileReplacement}
            />
          </div>
        </div>
      );
    }
    tab_default = (
      <Form>
        <FormGroup legendText="">
          <TextInput
            labelText="Name"
            value={item.name}
            id="form_element_name"
            invalid={item.name.length < 4}
            helperText="Das Element benötigt eine Kurzen namen"
            invalidText="Es muss ein Name vorhanden sein"
            disabled={!editable}
            onChange={(e) => changeFormField('name', e.target.value)}
          />
          <TextArea
            labelText="Beschreibung"
            value={item.description}
            id="form_element_description"
            helperText="Bitte Beschreibe kurz das Element"
            disabled={!editable}
            onChange={(e) =>
              changeFormField('description', e.target.value)
            }
          />
        </FormGroup>
        <FormGroup legendText="Kategorisierung">
          <CreatableSelect
            isMulti
            noOptionsMessage={() => "Wählen Sie"}
            formatCreateLabel={(i) => `"${i}" erstellen`}

            isDisabled={isLoading || !editable}
            isLoading={isLoading}
            styles={customStyles}
            onChange={handleCategoryChange}
            onCreateOption={handleAddCategory}
            options={(tags || []).map(e => { return {label: e.label, value: e.details.ID} })}
            value={(item.tags || []).map(e => { return {label: e.label, value: e.details.ID} }) }
          />
        </FormGroup>
        {isAdmin && (
          <FormGroup legendText="Gruppenzuordnung" helperText="Beschränkt die Verfügbarkeit auf einzelne Gruppen">
            <SelectableGroups selection={item.groups || [1]} onSelectionChange={handleChangeGroups} />
          </FormGroup>
        )}
        {isAdmin && (
          <FormGroup
            legendText="Kunde / Organisation"
            helperText={'Lässt das Element nur für einen bestimmten Kunden sichtbar erscheinen. „Öffentlich" bleibt für alle sichtbar.'}
          >
            {(() => {
              // react-select gives us a searchable input by default plus
              // automatic above/below menu placement via menuPlacement
              // = "auto", which we need because this select sits at the
              // very bottom of the form — the dropdown would otherwise
              // get clipped by the SideForm.
              //
              // The list is the customer org list returned by
              // /users/organisations plus a synthetic "Öffentlich"
              // entry at id=0 that maps to the public pool (no
              // org-scoped restriction).
              const options = [
                { value: 0, label: 'Öffentlich (Standard)' },
                ...((organisations || []).map((o) => ({ value: o.id, label: o.name }))),
              ];
              const current = options.find((o) => o.value === (item.organisation_id || 0))
                || options[0];
              return (
                <Select
                  inputId="form_element_organisation"
                  options={options}
                  value={current}
                  isSearchable
                  isClearable={false}
                  // "auto" picks above/below based on viewport room —
                  // critical because the form scrolls and the menu can
                  // otherwise fall off the bottom of the side panel.
                  menuPlacement="auto"
                  placeholder="Kunde suchen oder auswählen…"
                  noOptionsMessage={() => 'Keine Treffer'}
                  styles={customStyles}
                  onChange={(opt) => changeFormField('organisation_id', opt ? opt.value : 0)}
                />
              );
            })()}
          </FormGroup>
        )}
        {isAdmin && (
          <Checkbox
            labelText="Öffentlich sichtbar"
            id={`form_element_public`}
            onChange={(_, {checked}) => changeFormField('public', checked)}
            checked={item.public}
            disabled={!isAdmin}
          />
        )}
      </Form>
    );

    tab_advanced = (
      <Form>
        <FormGroup legendText="">
          <NumberInput
            disabled={item.type === 1 || !editable}
            value={item.default_duration}
            step={1}
            label="Anzeigedauer in Sekunden"
            allowEmpty={false}
            id="form_element_default_duration"
            invalidText="Der Angegebene Wert ist ungültig!"
            invalid={
              item.default_duration < 1 || item.default_duration > 5 * 60
            }
            helperText="Die Anzeigedauer die ein Element auf dem System eingebledet wird. (Kann später noch überschrieben werden)"
            onChange={(_e, {value, direction}) => {
              if (value > 0) {
                changeFormField(
                  'default_duration',
                  Number(value)
                );
              } else {
                changeFormField(
                  'default_duration',
                  direction === 'up'
                    ? item.default_duration + 1
                    : item.default_duration - 1
                );
              }
            }}
          />
        </FormGroup>
        <FormGroup
          legendText="Ausrichtung"
          // Pair elements pull their format from the pair partnership
          // (one landscape + one portrait), so the per-element radio
          // is meaningless here. We keep it visible but disabled and
          // explain why, instead of silently dropping the field.
          {...(item._pair_partner
            ? { helperText: 'Element ist Teil eines Pairs — die Ausrichtung wird durch das Quer-/Hochformat-Paar bestimmt.' }
            : {})}
        >
          <RadioButtonGroup
            disabled={!editable || Boolean(item._pair_partner)}
            valueSelected={item.orientation + 1 || 1}
            name="form_element_orientation"
            onChange={(e) => changeFormField('orientation', e - 1)}>
            <RadioButton value={1} labelText="Querformat" />
            <RadioButton value={2} labelText="Hochkant Darstellung" />
            <RadioButton value={3} labelText="Multi Darstellung" />
          </RadioButtonGroup>
        </FormGroup>
        {isAdmin && (
          <FormGroup legendText="Automatisch entfernen" helperText="Entferne das Element automatisch nach dem eingestellten Zeitraunm">
            <Checkbox
              id={`form_element_remove_after`}
              labelText="Automatisch löschen aktivieren"
              onChange={(_, {checked}) => changeFormField('remove_after', checked)}
              checked={item.remove_after}
            />
            <DatePicker
              id="form_element_remove_after_date"
              datePickerType="single"
              dateFormat="d.m.Y"
              minDate={today.format('DD.MM.YYYY')}
              locale="de"
              short={true}
              onChange={(e) => {
                let s = e[0].toLocaleDateString('en-CA');
                changeFormField('remove_date', s);
              }}
              value={[Date.parse(item.remove_date)]}>
              <DatePickerInput
                id="form_element_remove_after_date_input"
                placeholder={today.format('DD.MM.YYYY')}
                labelText="Startdatum"
                hideLabel={true}
                size="md"
              />
            </DatePicker>
          </FormGroup>
        )}
        {isAdmin && (
          <FormGroup legendText="Hervorheben" helperText="Element für einen bestimmten Zeitpunkt hervorheben">
            <Checkbox
              id={`form_element_highlight`}
              labelText="Element Hervorheben"
              onChange={(_, {checked}) => changeFormField('highlight', checked)}
              checked={item.highlight}
            />
            <DatePicker
              id="form_element_highlight_datespan"
              datePickerType="range"
              dateFormat="d.m.Y"
              minDate={today.format('DD.MM.YYYY')}
              locale="de"
              short={false}
              onChange={(e) => {
                let s = e[0].toLocaleDateString('en-CA');
                //changeFormField('highlight_start', s);
                setHighlightStart(e[0]);

                let t = "";
                if (e.length > 1) {
                  t = e[1].toLocaleDateString('en-CA');
                  setHighlightEnd(e[1]);
                } else {
                  setHighlightEnd("");
                }
                //changeFormField('highlight_end', t);
              }}
              value={[highlight_start, highlight_end]}>
              <DatePickerInput
                id="form_element_highlight_datespan_start"
                placeholder={today.format('DD.MM.YYYY')}
                labelText="Startdatum"
                hideLabel={true}
                size="md"
              />
              <DatePickerInput
                id="form_element_highlight_datespan_end"
                placeholder={today.format('DD.MM.YYYY')}
                labelText="End Datum"
                hideLabel={true}
                size="md"
              />
            </DatePicker>
          </FormGroup>
        )}
        {remove_element}
      </Form>
    );

    tab_pair = (
      <ElementPairAssignment
        item={item}
        editable={editable}
        onPairChanged={() => item?.model?.ID && load(item.model.ID)}
      />
    );

    tab_variants = (
      <ElementVariantsAssignment item={item} editable={editable} />
    );

    tab_usage = (
      <div className="status_tab">
        Das Element wird derzeit in{' '}
        {inStreamStatus.count > 0 ? inStreamStatus.count : 'keinem'}{' '}
        {inStreamStatus.count < 2 ? 'Stream' : 'Streams'} verwendet
        {inStreamStatus.count > 0 ? ':' : '.'}
        <br />
        <Row style={{ marginLeft: 0 }}>
          <Column>
            <UnorderedList>
              {inStreamStatus.streams.map((i) => (
                <ListItem key={`list_item_stream_${i.model.ID}`}>
                  <button
                    className="cds--link"
                    onClick={() => gotoStream(i.model.ID)}>
                    {i.name}
                  </button>
                </ListItem>
              ))}
            </UnorderedList>
          </Column>
        </Row>
      </div>
    );
  }


  return (
    <SideForm
      title={isMultiupload ? "Multi-Upload Element Form" : editable ? "Element Editor" : "Element Vorschau"}
      subTitle={isNew ? "neues Element erstellen" : editable ? "ausgewähltes Element bearbeiten" : "Element Vorschau"}
      onClose={onRequestClose}
      onSubmit={() => editable ? onSaveRequest() : onRequestClose()}
      primaryButton={editable ? 'Speichern': "Schließen"}
      primaryButtonDisabled={!unsaved || !editable}
      secondaryButton={unsaved === true ? 'Abbruch' : 'Verlassen'}>
      {is_saving ? (
        <LoadingPage />
      ) : isUploading ? (
        <LoadingPage progress={current_upload_progress} />
      ) : isCreating || isSaving ? (
        <LoadingPage title="Element wird auf Server bearbeitet. Bitte warten..." />
      ) : null}

      {img}
      <Tabs>
        <TabList
          aria-label="Element-Reiter"
          contained
        >
          <Tab id="tab_default">
            Allgemein
          </Tab>
          <Tab id="tab_advanced">
            Erweitert
          </Tab>
          <Tab id="tab_pair">
            Hoch+Quer
          </Tab>
          {isAdmin && (
            <Tab id="tab_variants">
              Varianten
            </Tab>
          )}
          <Tab id="tab_usage">
            Verwendung
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel className="tab-content">
            {tab_default}
          </TabPanel>
          <TabPanel className="tab-content">
            {tab_advanced}
          </TabPanel>
          <TabPanel className="tab-content">
            {tab_pair}
          </TabPanel>
          {isAdmin && (
            <TabPanel className="tab-content">
              {tab_variants}
            </TabPanel>
          )}
          <TabPanel className="tab-content">
            {tab_usage}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </SideForm>
  )
}

export default ElementForm;
